import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

export type UploadStage = 'idle' | 'uploading' | 'processing' | 'success' | 'error';

interface UploadProgressAlertProps {
  stage: UploadStage;
  progress: number;
  error: string | null;
  onRetry: () => void;
}

export function UploadProgressAlert({ stage, progress, error, onRetry }: UploadProgressAlertProps) {
  if (stage === 'idle') return null;

  return (
    <div className="space-y-3">
      {stage === 'success' && (
        <Alert className="border-green-500 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <AlertDescription className="text-green-800 font-medium">
            Receipt uploaded successfully!
          </AlertDescription>
        </Alert>
      )}

      {stage === 'error' && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="space-y-3">
            <p className="font-medium">{error}</p>
            <Button 
              onClick={onRetry}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {(stage === 'uploading' || stage === 'processing') && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">
              {stage === 'uploading' ? 'Uploading...' : 'Processing...'}
            </span>
            <span className="text-muted-foreground">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground">
            {stage === 'uploading' 
              ? 'Uploading to your account' 
              : 'Analyzing receipt details'}
          </p>
        </div>
      )}
    </div>
  );
}
